import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

// Solar (Iconify) — clean outline icons, one unique glyph per route.
// Names are resolved at runtime from api.iconify.design: a name that does not
// exist renders a blank box with no error, so verify new ones before shipping
// (curl -s "https://api.iconify.design/solar.json?icons=<name-without-prefix>").
const navIcon = (icon: `solar:${string}`) => <Iconify width={22} icon={icon} />;

const ICONS = {
  /** Custodial group — vault */
  custodial: navIcon('solar:safe-2-linear'),
  /** Dashboard / at-a-glance */
  overview: navIcon('solar:widget-5-linear'),
  /** Wallets (Custodial / Checkout / Batch Payout) */
  wallets: navIcon('solar:wallet-linear'),
  /** Transaction list */
  transactions: navIcon('solar:bill-list-linear'),
  customers: navIcon('solar:users-group-rounded-linear'),
  checkout: navIcon('solar:cart-large-2-linear'),
  checkoutProducts: navIcon('solar:box-minimalistic-linear'),
  checkoutSessions: navIcon('solar:clipboard-list-linear'),
  invoices: navIcon('solar:document-text-linear'),
  reports: navIcon('solar:document-add-linear'),
  gas: navIcon('solar:gas-station-linear'),
  billing: navIcon('solar:wallet-money-linear'),
  webhooks: navIcon('solar:link-minimalistic-2-linear'),
  keys: navIcon('solar:key-minimalistic-square-2-linear'),
  settings: navIcon('solar:settings-linear'),
  user: navIcon('solar:user-linear'),
  organization: navIcon('solar:shield-keyhole-linear'),
  terminals: navIcon('solar:smartphone-2-linear'),
  swap: navIcon('solar:round-transfer-horizontal-linear'),
  transfer: navIcon('solar:transfer-vertical-linear'),
  customerSwaps: navIcon('solar:card-transfer-linear'),
  batchPayouts: navIcon('solar:card-send-linear'),
  treasuryAutoConversion: navIcon('solar:refresh-circle-linear'),
} as const;

// ----------------------------------------------------------------------

// NOTE: feature entries are gated on org flags in layouts/dashboard/layout.tsx,
// and the gate is matched by PATH at a specific depth. Moving an entry between
// the top level and a `children` array means moving its check too — otherwise
// it silently becomes visible to every org.
//
//   top level: Swaps (valiantEnabled), Batch Payout (batchPayoutsEnabled)
//   children:  Auto-Conversion, both the Custodial and Checkout ones
//                (nearIntentsEnabled);
//              Deposit Swaps (nearIntentsEnabled && debugMode);
//              Customer Swaps (customerSwapsEnabled);
//              Billing (canViewBilling)
export const navData = [
  {
    subheader: 'Pages',
    items: [
      {
        title: 'Custodial',
        path: paths.dashboard.overview,
        icon: ICONS.custodial,
        children: [
          { title: 'Overview', path: paths.dashboard.overview, icon: ICONS.overview },
          { title: 'Transactions', path: paths.dashboard.transactions, icon: ICONS.transactions },
          { title: 'Wallets', path: paths.dashboard.wallets, icon: ICONS.wallets },
          { title: 'Customers', path: paths.dashboard.customers, icon: ICONS.customers },
          // Gated as CHILDREN in layouts/dashboard/layout.tsx:
          // Deposit Swaps → nearIntentsEnabled && debugMode;
          // Auto-Conversion → nearIntentsEnabled.
          // Moving either back to the top level means moving its gate too,
          // or it becomes visible to every org.
          {
            title: 'Deposit Swaps',
            path: paths.dashboard.nearIntents.root,
            icon: ICONS.swap,
          },
          {
            title: 'Customer Swaps',
            path: paths.dashboard.customerSwaps.root,
            icon: ICONS.customerSwaps,
          },
          {
            title: 'Auto-Conversion',
            path: paths.dashboard.treasuryAutoConversion,
            icon: ICONS.treasuryAutoConversion,
          },
          { title: 'Webhooks', path: paths.dashboard.webhooks, icon: ICONS.webhooks },
          { title: 'Reports', path: paths.dashboard.reports.default, icon: ICONS.reports },
        ],
      },
      {
        title: 'Checkout',
        path: paths.dashboard.checkout.root,
        icon: ICONS.checkout,
        children: [
          {
            title: 'Overview',
            path: paths.dashboard.checkout.analytics,
            icon: ICONS.overview,
          },
          {
            title: 'Transactions',
            path: paths.dashboard.checkout.transactions,
            icon: ICONS.transactions,
          },
          {
            title: 'Wallets',
            path: paths.dashboard.checkout.wallets,
            icon: ICONS.wallets,
          },
          {
            title: 'Products',
            path: paths.dashboard.checkout.products,
            icon: ICONS.checkoutProducts,
          },
          {
            title: 'Checkout Sessions',
            path: paths.dashboard.checkout.sessions,
            icon: ICONS.checkoutSessions,
          },
          { title: 'Invoices', path: paths.dashboard.invoices.root, icon: ICONS.invoices },
          // Gated as a CHILD in layouts/dashboard/layout.tsx on nearIntentsEnabled,
          // matched by path — see the note at the top of this file.
          {
            title: 'Auto-Conversion',
            path: paths.dashboard.checkout.autoConversion,
            icon: ICONS.treasuryAutoConversion,
          },
          { title: 'Terminals', path: paths.dashboard.terminals, icon: ICONS.terminals },
          { title: 'Reports', path: paths.dashboard.reports.pay, icon: ICONS.reports },
        ],
      },
      {
        title: 'Batch Payout',
        path: paths.dashboard.batchPayouts.root,
        icon: ICONS.batchPayouts,
        children: [
          {
            title: 'Overview',
            path: paths.dashboard.batchPayouts.root,
            icon: ICONS.overview,
          },
          {
            title: 'Transactions',
            path: paths.dashboard.batchPayouts.transactions,
            icon: ICONS.transactions,
          },
          {
            title: 'Wallets',
            path: paths.dashboard.batchPayouts.funding,
            icon: ICONS.wallets,
          },
          {
            title: 'Build',
            path: paths.dashboard.batchPayouts.build,
            icon: navIcon('solar:pen-new-square-linear'),
          },
          {
            title: 'Reports',
            path: paths.dashboard.reports.payouts,
            icon: ICONS.reports,
          },
        ],
      },
      {
        title: 'Swaps',
        path: paths.dashboard.swap.root,
        icon: ICONS.swap,
        children: [
          { title: 'Swaps', path: paths.dashboard.swap.root, icon: ICONS.swap },
          { title: 'Reports', path: paths.dashboard.reports.swaps, icon: ICONS.reports },
        ],
      },
      {
        title: 'Settings',
        path: paths.dashboard.user,
        icon: ICONS.settings,
        children: [
          { title: 'User Settings', path: paths.dashboard.user, icon: ICONS.user },
          {
            title: 'Organization Security',
            path: paths.dashboard.organization,
            icon: ICONS.organization,
          },
          {
            title: 'Transfer between wallets',
            path: paths.dashboard.transfer,
            icon: ICONS.transfer,
          },
          // Gated as a CHILD in layouts/dashboard/layout.tsx on canViewBilling.
          { title: 'Billing', path: paths.dashboard.billing.root, icon: ICONS.billing },
          { title: 'Gas Stations', path: paths.dashboard.gas, icon: ICONS.gas },
          { title: 'API Key', path: paths.dashboard.keys, icon: ICONS.keys },
        ],
      },
    ],
  },
];
